import { useState, useEffect, type ChangeEvent } from 'react';
import { apiFetch } from '@/utils/api';
import { useNotify } from '@/context/NotificationContext';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { Trash2, Plus, Image as ImageIcon, Save, X, ChevronLeft, RefreshCw, Package, Sparkles, DollarSign, Upload } from 'lucide-react';

interface SkinsManagerProps {
    onBack: () => void;
}

export const SkinsManager = ({ onBack }: SkinsManagerProps) => {
    const { showNotification } = useNotify();
    const { impactOccurred, notificationOccurred } = useHapticFeedback();

    const [skins, setSkins] = useState<any[]>([]);
    const [packages, setPackages] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [refreshSeed, setRefreshSeed] = useState(Date.now()); // Global refresh seed for images
    const [isSaving, setIsSaving] = useState(false);

    // Navigation State
    const [activeTab, setActiveTab] = useState<'skins' | 'packages'>('packages'); // Default to packages
    const [selectedPackage, setSelectedPackage] = useState<any | null>(null);

    // Form State
    const [newName, setNewName] = useState('');
    const [newDesc, setNewDesc] = useState('');
    const [newPrice, setNewPrice] = useState('0');
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [isFree, setIsFree] = useState(false);
    const [isPremium, setIsPremium] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [skinsRes, pkgsRes] = await Promise.all([
                apiFetch('/api/skins'), // Fetches ALL skins initially
                apiFetch('/api/skins/packages')
            ]);

            const skinsData = await skinsRes.json();
            const pkgsData = await pkgsRes.json();

            if (skinsData.success) setSkins(skinsData.skins);
            if (pkgsData.success) setPackages(pkgsData.packages);

        } catch (e) {
            console.error(e);
            showNotification('Error', 'Failed to fetch data', 'error');
        } finally {
            setLoading(false);
            setRefreshSeed(Date.now());
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleAdd = async () => {
        if (isSaving) return;
        if (!newName || !imageFile) {
            notificationOccurred('error');
            return showNotification('Error', 'Name and Image file are required', 'error');
        }

        const formData = new FormData();
        formData.append('name', newName);
        formData.append('image', imageFile);

        let endpoint = '/api/skins/add';

        if (activeTab === 'packages' && !selectedPackage) {
            // Adding a new Package
            endpoint = '/api/skins/packages/add';
            formData.append('description', newDesc);
        } else {
            // Adding a Skin (either from 'skins' tab or determining package)
            formData.append('price', isFree ? '0' : newPrice);
            formData.append('is_free', String(isFree));
            formData.append('is_premium', String(isPremium));
            formData.append('description', newDesc);
            formData.append('description', newDesc);
            formData.append('skin_type', 'profile');

            // If adding inside a package, attach package_id
            if (selectedPackage) {
                formData.append('package_id', selectedPackage.package_id);
            }
        }

        setIsSaving(true);
        try {
            const res = await apiFetch(endpoint, {
                method: 'POST',
                body: formData
            });

            const data = await res.json();
            if (data.success) {
                impactOccurred('medium');
                showNotification('Success', 'Item added successfully', 'success');
                setShowAddModal(false);
                resetForm();
                fetchData();
            } else {
                notificationOccurred('error');
                showNotification('Error', data.error || 'Failed to add item', 'error');
            }
        } catch (e) {
            console.error(e);
            notificationOccurred('error');
            showNotification('Error', 'Network error', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const resetForm = () => {
        setNewName('');
        setNewDesc('');
        setNewPrice('0');
        setImageFile(null);
        setPreviewUrl(null);
        setIsFree(false);
        setIsPremium(false);
    };

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setImageFile(file);
            const url = URL.createObjectURL(file);
            setPreviewUrl(url);
        }
    };

    const deletePackage = async (pkgId: number) => {
        if (!confirm('Are you sure you want to delete this package?')) return;
        try {
            const res = await apiFetch(`/api/skins/packages/${pkgId}`, { method: 'DELETE' });
            const data = await res.json();
            if (data.success) {
                showNotification('Success', 'Package deleted', 'success');
                if (selectedPackage?.package_id === pkgId) setSelectedPackage(null);
                fetchData();
            } else {
                showNotification('Error', data.error, 'error');
            }
        } catch (e) { console.error(e); }
    };

    // Filter skins if a package is selected
    const displayedSkins = selectedPackage
        ? skins.filter(s => s.package_id == selectedPackage.package_id)
        : skins;

    // View: Detail Mode (Inside a Package)
    if (selectedPackage) {
        return (
            <div className="space-y-6">
                <div className="flex items-center gap-3 border-b border-white/5 pb-4">
                    <button
                        onClick={() => setSelectedPackage(null)}
                        className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                    >
                        <ChevronLeft size={20} />
                    </button>
                    <div>
                        <h2 className="text-xl font-bold text-white flex items-center gap-2">
                            {selectedPackage.name}
                            <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-md border border-blue-500/30">Package</span>
                        </h2>
                        <p className="text-xs text-white/40">{selectedPackage.description}</p>
                    </div>
                    <div className="ml-auto flex gap-2">
                        <button
                            onClick={() => deletePackage(selectedPackage.package_id)}
                            className="bg-red-500/10 hover:bg-red-500/20 text-red-400 px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 transition-all"
                        >
                            <Trash2 size={16} /> Delete Package
                        </button>
                        <button
                            onClick={() => { resetForm(); setShowAddModal(true); }}
                            className="bg-[#3390EC] hover:bg-[#3390EC]/80 text-white px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 transition-all active:scale-95"
                        >
                            <Plus size={16} /> Add Skin to Package
                        </button>
                    </div>
                </div>

                {/* Skins within Package */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {displayedSkins.map(skin => (
                        <div key={skin.id} className="relative group bg-[#1c1c1e] rounded-xl overflow-hidden border border-white/5 hover:border-white/10 transition-all">
                            <div className="aspect-[3/1] bg-black/50 relative">
                                <img
                                    src={`${skin.image_url}?refresh=${refreshSeed}`}
                                    alt={skin.name}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        const retries = parseInt(target.getAttribute('data-retries') || '0');
                                        if (retries < 3) {
                                            setTimeout(() => {
                                                target.setAttribute('data-retries', (retries + 1).toString());
                                                target.src = `${skin.image_url}?refresh=${Date.now()}&retry=${retries + 1}`;
                                            }, 1000);
                                        }
                                    }}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-60" />
                                <div className="absolute top-2 right-2 flex gap-1">
                                    {skin.is_premium && <span className="bg-amber-500 text-black text-[9px] font-bold px-1.5 py-0.5 rounded">PREMIUM</span>}
                                    {skin.is_free && <span className="bg-green-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">FREE</span>}
                                </div>
                            </div>
                            <div className="p-3">
                                <h3 className="font-bold text-sm text-white truncate">{skin.name}</h3>
                                <div className="flex items-center justify-between mt-1">
                                    <span className="text-xs text-blue-400 font-mono">{skin.is_free ? 'Free' : `${skin.price} GRAM`}</span>
                                    <span className={`text-[10px] uppercase font-bold ${skin.is_active ? 'text-green-500' : 'text-red-500'}`}>
                                        {skin.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                    {displayedSkins.length === 0 && (
                        <div className="col-span-full py-12 text-center border-2 border-dashed border-white/5 rounded-2xl text-white/20">
                            No skins in this package yet. Add one!
                        </div>
                    )}
                </div>
                {/* Re-use Main Add Modal (It adapts based on selectedPackage) */}
                {showAddModal && renderAddModal()}
            </div>
        );
    }

    // Default View (List of Packages or All Skins)
    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={onBack}
                        className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
                    >
                        <ChevronLeft size={20} className="text-white/60" />
                    </button>
                    <div>
                        <h2 className="text-xl font-bold text-white">Profile Covers Manager</h2>
                        <p className="text-xs text-white/40">Manage marketplace skins and packages</p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <div className="flex bg-white/5 rounded-lg p-1">
                        <button
                            onClick={() => setActiveTab('packages')}
                            className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${activeTab === 'packages' ? 'bg-[#3390EC] text-white' : 'text-white/40 hover:text-white'}`}
                        >
                            Packages
                        </button>
                        <button
                            onClick={() => setActiveTab('skins')}
                            className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${activeTab === 'skins' ? 'bg-[#3390EC] text-white' : 'text-white/40 hover:text-white'}`}
                        >
                            All Skins
                        </button>
                    </div>
                    <button
                        onClick={() => { resetForm(); setShowAddModal(true); }}
                        className="bg-[#3390EC] hover:bg-[#3390EC]/80 text-white px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 transition-all active:scale-95"
                    >
                        <Plus size={16} /> Add {activeTab === 'skins' ? 'Skin' : 'Package'}
                    </button>
                </div>
            </div>

            {/* List */}
            {loading ? (
                <div className="text-center py-12 text-white/40 animate-pulse">Loading data...</div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {activeTab === 'packages' ? packages.map(pkg => (
                        <div
                            key={pkg.package_id}
                            onClick={() => setSelectedPackage(pkg)}
                            className="relative group bg-[#1c1c1e] rounded-xl overflow-hidden border border-white/5 hover:border-white/20 hover:scale-[1.02] transition-all cursor-pointer shadow-lg hover:shadow-xl hover:shadow-black/50"
                        >
                            <div className="aspect-[3/1] bg-black/50 relative">
                                <img
                                    src={pkg.cover_image_url ? `${pkg.cover_image_url}?refresh=${refreshSeed}` : '/skins/packages/default.jpg'}
                                    alt={pkg.name}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        const retries = parseInt(target.getAttribute('data-retries') || '0');
                                        if (retries < 5) {
                                            setTimeout(() => {
                                                target.setAttribute('data-retries', (retries + 1).toString());
                                                target.src = `${pkg.cover_image_url}?refresh=${Date.now()}&retry=${retries + 1}`;
                                            }, 1000);
                                        } else {
                                            target.src = '/skins/packages/default.jpg';
                                        }
                                    }}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-60" />
                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                    <span className="bg-white/10 backdrop-blur-md px-3 py-1 rounded-full text-xs font-bold border border-white/20">Manage Contents</span>
                                </div>
                                <button
                                    onClick={(e) => { e.stopPropagation(); deletePackage(pkg.package_id); }}
                                    className="absolute top-2 right-2 bg-red-500/20 hover:bg-red-500 text-red-500 p-1.5 rounded-lg backdrop-blur-md transition-colors opacity-0 group-hover:opacity-100"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                            <div className="p-3">
                                <h3 className="font-bold text-sm text-white truncate">{pkg.name}</h3>
                                <div className="flex justify-between items-center mt-1">
                                    <p className="text-[10px] text-white/40 line-clamp-1">{pkg.description}</p>
                                    <span className="text-[9px] bg-white/5 px-1.5 py-0.5 rounded text-white/40">
                                        {skins.filter(s => s.package_id === pkg.package_id).length} Skins
                                    </span>
                                </div>
                            </div>
                        </div>
                    )) : skins.map(skin => (
                        <div key={skin.id} className="relative group bg-[#1c1c1e] rounded-xl overflow-hidden border border-white/5 hover:border-white/10 transition-all">
                            <div className="aspect-[3/1] bg-black/50 relative">
                                <img
                                    src={`${skin.image_url}?refresh=${refreshSeed}`}
                                    alt={skin.name}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        const retries = parseInt(target.getAttribute('data-retries') || '0');
                                        if (retries < 3) {
                                            setTimeout(() => {
                                                target.setAttribute('data-retries', (retries + 1).toString());
                                                target.src = `${skin.image_url}?refresh=${Date.now()}&retry=${retries + 1}`;
                                            }, 1000);
                                        }
                                    }}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-60" />
                                <div className="absolute top-2 right-2 flex gap-1">
                                    {skin.is_premium && <span className="bg-amber-500 text-black text-[9px] font-bold px-1.5 py-0.5 rounded">PREMIUM</span>}
                                    {skin.is_free && <span className="bg-green-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">FREE</span>}
                                </div>
                            </div>
                            <div className="p-3">
                                <h3 className="font-bold text-sm text-white truncate">{skin.name}</h3>
                                <div className="flex items-center justify-between mt-1">
                                    <span className="text-xs text-blue-400 font-mono">{skin.is_free ? 'Free' : `${skin.price} GRAM`}</span>
                                    <span className={`text-[10px] uppercase font-bold ${skin.is_active ? 'text-green-500' : 'text-red-500'}`}>
                                        {skin.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}

                    {((activeTab === 'skins' && skins.length === 0) || (activeTab === 'packages' && packages.length === 0)) && (
                        <div className="col-span-full py-12 text-center border-2 border-dashed border-white/5 rounded-2xl text-white/20">
                            No {activeTab} found. Add some to get started.
                        </div>
                    )}
                </div>
            )}

            {showAddModal && renderAddModal()}
        </div>
    );

    function renderAddModal() {
        const isPackageMode = activeTab === 'packages' && !selectedPackage;
        return (
            <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
                <div className="bg-[#111113] w-full max-w-lg rounded-[32px] border border-white/10 shadow-[0_0_100px_rgba(51,144,236,0.15)] overflow-hidden animate-in slide-in-from-bottom-8 zoom-in-95 duration-300 relative">
                    {/* Background glow fx */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-[80px] -z-10 pointer-events-none" />
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/10 rounded-full blur-[80px] -z-10 pointer-events-none" />

                    <div className="p-6 border-b border-white/5 flex items-center justify-between relative z-10">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
                                {isPackageMode ? <Package size={20} className="text-white" /> : <ImageIcon size={20} className="text-white" />}
                            </div>
                            <div>
                                <h3 className="font-black text-xl text-white tracking-tight">
                                    {selectedPackage ? 'Add to Collection' : (isPackageMode ? 'New Package' : 'Create Skin')}
                                </h3>
                                <p className="text-[10px] text-white/50 uppercase tracking-widest font-bold">
                                    {selectedPackage ? selectedPackage.name : 'Asset Manager'}
                                </p>
                            </div>
                        </div>
                        <button onClick={() => setShowAddModal(false)} className="w-8 h-8 flex items-center justify-center bg-white/5 hover:bg-white/10 rounded-full transition-colors">
                            <X size={16} className="text-white/60" />
                        </button>
                    </div>

                    <div className="p-6 space-y-6 relative z-10">
                        {/* Futuristic Inputs */}
                        <div className="space-y-4">
                            <div className="relative group">
                                <input
                                    value={newName}
                                    onChange={e => setNewName(e.target.value)}
                                    placeholder=" "
                                    className="peer w-full bg-[#1c1c1e] border border-white/5 rounded-2xl px-5 py-4 text-sm text-white focus:border-blue-500 focus:bg-[#202022] outline-none transition-all placeholder-transparent"
                                />
                                <label className="absolute left-5 top-4 text-xs font-bold text-white/40 uppercase transition-all peer-focus:-top-2.5 peer-focus:text-[10px] peer-focus:text-blue-400 peer-focus:bg-[#111113] peer-focus:px-2 peer-[:not(:placeholder-shown)]:-top-2.5 peer-[:not(:placeholder-shown)]:text-[10px] peer-[:not(:placeholder-shown)]:text-white/60 peer-[:not(:placeholder-shown)]:bg-[#111113] peer-[:not(:placeholder-shown)]:px-2 pointer-events-none">
                                    {isPackageMode ? 'Package Name' : 'Skin Name'}
                                </label>
                            </div>

                            <div className="relative group">
                                <input
                                    value={newDesc}
                                    onChange={e => setNewDesc(e.target.value)}
                                    placeholder=" "
                                    className="peer w-full bg-[#1c1c1e] border border-white/5 rounded-2xl px-5 py-4 text-sm text-white focus:border-purple-500 focus:bg-[#202022] outline-none transition-all placeholder-transparent"
                                />
                                <label className="absolute left-5 top-4 text-xs font-bold text-white/40 uppercase transition-all peer-focus:-top-2.5 peer-focus:text-[10px] peer-focus:text-purple-400 peer-focus:bg-[#111113] peer-focus:px-2 peer-[:not(:placeholder-shown)]:-top-2.5 peer-[:not(:placeholder-shown)]:text-[10px] peer-[:not(:placeholder-shown)]:text-white/60 peer-[:not(:placeholder-shown)]:bg-[#111113] peer-[:not(:placeholder-shown)]:px-2 pointer-events-none">
                                    Description
                                </label>
                            </div>
                        </div>

                        {/* Image/Video Uploader */}
                        <div>
                            <div className="relative group cursor-pointer overflow-hidden rounded-2xl border-2 border-dashed border-white/10 hover:border-blue-500/50 bg-[#1c1c1e]/50 hover:bg-[#1c1c1e] transition-all">
                                <input type="file" accept="image/*,video/mp4,video/webm" onChange={handleFileChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-20" />
                                
                                {previewUrl ? (
                                    <div className="relative aspect-[21/9] w-full">
                                        {imageFile?.type.startsWith('video/') ? (
                                            <video src={previewUrl} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" autoPlay loop muted playsInline />
                                        ) : (
                                            <img src={previewUrl} alt="Preview" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                                        )}
                                        <div className="absolute inset-0 bg-gradient-to-t from-[#111113] to-transparent" />
                                        <div className="absolute flex items-center justify-center inset-0 z-10 pointer-events-none">
                                            <span className="bg-black/50 backdrop-blur-md text-white text-xs font-bold px-4 py-2 rounded-full border border-white/10">Change Image</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="px-5 py-10 flex flex-col items-center justify-center gap-3">
                                        <div className="w-14 h-14 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-400 group-hover:scale-110 group-hover:bg-blue-500/20 transition-all">
                                            <Upload size={24} />
                                        </div>
                                        <div className="text-center">
                                            <p className="text-sm font-bold text-white mb-1">Upload Asset</p>
                                            <p className="text-[10px] text-white/40 uppercase tracking-widest">JPG/PNG/WEBP/MP4 (Max 20MB)</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Toggles and Price */}
                        {!isPackageMode && (
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                <label className={`flex flex-col items-center justify-center gap-2 p-4 rounded-2xl border cursor-pointer transition-all ${isFree ? 'bg-green-500/10 border-green-500/30 shadow-[0_0_20px_rgba(34,197,94,0.1)]' : 'bg-[#1c1c1e] border-white/5 hover:border-white/10'}`}>
                                    <input type="checkbox" checked={isFree} onChange={e => { setIsFree(e.target.checked); if (e.target.checked) setNewPrice('0'); }} className="hidden" />
                                    <span className={`text-[10px] uppercase font-black tracking-widest ${isFree ? 'text-green-500' : 'text-white/40'}`}>Free Drop</span>
                                    <span className={`text-xs ${isFree ? 'text-green-400' : 'text-white/60'}`}>{isFree ? 'Active' : 'Disabled'}</span>
                                </label>

                                <label className={`flex flex-col items-center justify-center gap-2 p-4 rounded-2xl border cursor-pointer transition-all ${isPremium ? 'bg-amber-500/10 border-amber-500/30 shadow-[0_0_20px_rgba(245,158,11,0.1)]' : 'bg-[#1c1c1e] border-white/5 hover:border-white/10'}`}>
                                    <input type="checkbox" checked={isPremium} onChange={e => setIsPremium(e.target.checked)} className="hidden" />
                                    <span className={`text-[10px] uppercase font-black tracking-widest ${isPremium ? 'text-amber-500' : 'text-white/40'}`}>Premium</span>
                                    <span className={`text-xs ${isPremium ? 'text-amber-400' : 'text-white/60'}`}>{isPremium ? 'Active' : 'Disabled'}</span>
                                </label>
                                
                                {!isFree && (
                                    <div className="col-span-2 relative group animate-in slide-in-from-bottom-2">
                                        <input
                                            type="number"
                                            value={newPrice}
                                            onChange={e => setNewPrice(e.target.value)}
                                            placeholder=" "
                                            className="peer w-full bg-[#1c1c1e] border border-white/5 rounded-2xl px-5 py-4 pl-12 text-lg font-bold text-white focus:border-blue-500 focus:bg-[#202022] outline-none transition-all placeholder-transparent"
                                        />
                                        <div className="absolute left-5 top-[18px] text-blue-500 pointer-events-none">
                                            <DollarSign size={20} />
                                        </div>
                                        <label className="absolute left-12 top-4.5 text-xs font-bold text-white/40 uppercase transition-all peer-focus:-top-2.5 peer-focus:left-5 peer-focus:text-[10px] peer-focus:text-blue-400 peer-focus:bg-[#111113] peer-focus:px-2 peer-[:not(:placeholder-shown)]:-top-2.5 peer-[:not(:placeholder-shown)]:left-5 peer-[:not(:placeholder-shown)]:text-[10px] peer-[:not(:placeholder-shown)]:text-white/60 peer-[:not(:placeholder-shown)]:bg-[#111113] peer-[:not(:placeholder-shown)]:px-2 pointer-events-none">
                                            Price (GRAM)
                                        </label>
                                    </div>
                                )}
                            </div>
                        </div>
                        )}
                    </div>

                    <div className="p-6 pt-2 relative z-10 flex gap-3">
                        <button
                            disabled={isSaving}
                            onClick={handleAdd}
                            className={`w-full py-4 rounded-2xl font-bold text-white transition-all flex items-center justify-center gap-2 ${isSaving ? 'bg-white/10 text-white/40 cursor-not-allowed' : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:opacity-90 shadow-lg shadow-blue-500/25 active:scale-[0.98]'}`}
                        >
                            {isSaving ? (
                                <RefreshCw size={20} className="animate-spin" />
                            ) : (
                                <Sparkles size={20} />
                            )}
                            {isSaving ? 'Deploying Asset...' : (isPackageMode ? 'Deploy Package' : 'Publish Skin')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }
};
